import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  dashboardRequest,
  DashboardRequestError,
} from "../providers/aws/client";
import { soundQueryKey } from "./api";
import type { AuditionRating } from "./auditionReviews";

export type SaveAuditionRatingInput = Pick<
  AuditionRating,
  | "pack_id"
  | "manifest_digest"
  | "voice_id"
  | "profile_id"
  | "seed"
  | "sample_version"
  | "render_profile_digest"
  | "rating"
  | "comment"
>;

export function saveAuditionRating({
  pack_id,
  ...body
}: SaveAuditionRatingInput) {
  return dashboardRequest<{ rating: AuditionRating }>(
    `/sound/auditions/${encodeURIComponent(pack_id)}/ratings`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function useSaveAuditionRating() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveAuditionRating,
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: soundQueryKey }),
  });
}

export function auditionRatingError(error: unknown) {
  if (error instanceof DashboardRequestError) {
    if (error.status === 401 || error.status === 403) {
      return "Administrator access is required to save a review. Sign in and try again.";
    }
    if (error.status === 404 || error.status === 409) {
      return "This audition pack changed. Refresh samples and listen to the current version before rating it.";
    }
  }
  return "The review could not be saved. Your selection is preserved; try again.";
}
